"use client";

import { useState } from "react";

import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";

import { subscribeToWaitlist } from "@/shared/api/waitlist";
import { Typography } from "@/shared/ui";

export function EmailSubscriptionForm() {
    const t = useTranslations("WakeWindowsTease.form");
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState("");

    const locale = useLocale();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        try {
            const response = await subscribeToWaitlist({
                email,
                firstName,
                lastName,
                locale,
                marketingConsent: true, // Always set to true as users agree by submitting
            });

            if (response.success) {
                setIsSuccess(true);
                setEmail("");
                setFirstName("");
                setLastName("");
            } else {
                setError(response.error || "Failed to subscribe");
            }
        } catch (error: any) {
            console.error("Error subscribing to waitlist:", error);
            setError(error.message || "An unexpected error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-auto shadow-lg border border-gray-100">
            {isSuccess ? (
                <div className="text-center">
                    <div className="mb-6">
                        <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                            <svg
                                className="w-6 h-6 text-green-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                            </svg>
                        </div>
                        <Typography className="mb-2" variant="title3">
                            {t("thankYou")}
                        </Typography>
                        <Typography variant="body">{t("successMessage")}</Typography>
                    </div>
                </div>
            ) : (
                <form className="mt-4" onSubmit={handleSubmit}>
                    <Typography className="mb-4" variant="title3">
                        {t("title")}
                    </Typography>
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-black mb-1" htmlFor="firstName">
                                    {t("firstName")}
                                </label>
                                <input
                                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
                                    id="firstName"
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder={t("firstNamePlaceholder")}
                                    type="text"
                                    value={firstName}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-black mb-1" htmlFor="lastName">
                                    {t("lastName")}
                                </label>
                                <input
                                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
                                    id="lastName"
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder={t("lastNamePlaceholder")}
                                    type="text"
                                    value={lastName}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-black mb-1" htmlFor="email">
                                {t("email")}
                            </label>
                            <input
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                                id="email"
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t("emailPlaceholder")}
                                required
                                type="email"
                                value={email}
                            />
                        </div>

                        <div className="mt-4">
                            <p className="text-sm text-gray-600 italic">{t("consentText")}</p>
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    <button
                        className={`w-full mt-6 px-6 py-3 bg-primary text-white rounded-lg font-medium text-lg
                            ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-primary-600"}
                            transition-colors duration-200`}
                        disabled={isSubmitting}
                        type="submit"
                    >
                        {isSubmitting ? t("submittingButton") : t("submitButton")}
                    </button>
                    <p className="text-xs text-gray-500 mt-3 text-center">{t("privacyNote")}</p>
                </form>
            )}
        </div>
    );
}
